import { useState } from 'react'
import { toast } from 'sonner'
import { AlertTriangle, Trash2 } from 'lucide-react'
import { apiDeleteMyAccount, apiFieldError, isApiError } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export const SESSION_EXPIRED_MESSAGE = 'Votre session a expiré, veuillez vous reconnecter'
export const DELETE_RATE_LIMIT_MESSAGE =
  'Trop de tentatives de confirmation du mot de passe. Réessayez dans quelques minutes.'

export const DELETE_ACCOUNT_WARNING =
  "Cette action est immédiate et irréversible. Votre compte, vos conversations avec l'assistant et vos accès sont supprimés. Les demandes de complément et relances que vous avez envoyées restent dans l'historique des campagnes, sans votre nom."

// Story 8.5 — Paramètres → Mon compte : informations du compte + suppression définitive (droit à l'oubli).
export function AccountSettings() {
  const { user, logout } = useAuth()
  const [open, setOpen] = useState(false)
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleOpenChange = (next: boolean) => {
    if (loading) return
    setOpen(next)
    if (!next) {
      setPassword('')
      setError(null)
    }
  }

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!password || loading) return
    setLoading(true)
    setError(null)
    try {
      await apiDeleteMyAccount(password)
      toast.success('Votre compte a été supprimé')
      logout()
    } catch (err: unknown) {
      setLoading(false)
      if (!isApiError(err)) {
        toast.error('Erreur lors de la suppression du compte')
        return
      }
      if (err.status === 401) {
        // Session expirée / compte désactivé : pas de message inline, on déconnecte.
        toast.error(SESSION_EXPIRED_MESSAGE)
        logout()
      } else if (err.status === 429) {
        // Message fixe : un 429 peut venir d'un autre limiteur (ou d'un proxy, corps non JSON).
        setError(DELETE_RATE_LIMIT_MESSAGE)
      } else if (err.status === 422) {
        setError(apiFieldError(err, 'password') ?? err.message)
      } else if (err.status === 403 || err.status === 409) {
        setError(err.message)
      } else {
        toast.error('Erreur lors de la suppression du compte')
      }
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Mon compte</h2>
        <p className="text-sm text-muted-foreground">Informations de votre compte utilisateur.</p>
      </div>

      <div className="space-y-2 rounded-lg border bg-white/70 p-4 text-sm">
        <div className="flex gap-2">
          <span className="w-16 text-muted-foreground">Nom</span>
          <span className="font-medium">{user?.name || '-'}</span>
        </div>
        <div className="flex gap-2">
          <span className="w-16 text-muted-foreground">Email</span>
          <span className="font-medium">{user?.email}</span>
        </div>
      </div>

      <div className={cn('space-y-3 rounded-lg border border-red-200 bg-red-50/60 p-4')}>
        <div className="flex items-center gap-2 text-red-700">
          <AlertTriangle className="h-4 w-4" />
          <h3 className="font-semibold">Zone de danger</h3>
        </div>
        <p className="text-sm text-red-900/80">
          La suppression de votre compte est définitive et ne peut pas être annulée.
        </p>
        <Button variant="destructive" onClick={() => setOpen(true)}>
          <Trash2 className="h-4 w-4" />
          Supprimer mon compte
        </Button>
      </div>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent>
          <form onSubmit={handleDelete} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Supprimer mon compte</DialogTitle>
              <DialogDescription>{DELETE_ACCOUNT_WARNING}</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="delete-account-password">Confirmez avec votre mot de passe</Label>
              <Input
                id="delete-account-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
              {error && (
                <p role="alert" className="text-sm text-red-600">
                  {error}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={loading}>
                Annuler
              </Button>
              <Button type="submit" variant="destructive" disabled={!password || loading}>
                {loading ? 'Suppression…' : 'Supprimer définitivement'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
